import React, { useState } from 'react';
import { Calendar, MapPin, User, Building2, Phone, Mail, FileText, Camera, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const ReceePrintPage = ({recceData}) => {

    console.log(recceData, "recceData")
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'onGoing': return <AlertCircle className="w-4 h-4 text-yellow-600" />;
            case 'completed': return <CheckCircle className="w-4 h-4 text-green-600" />;
            case 'rejected': return <XCircle className="w-4 h-4 text-red-600" />;
            default: return <AlertCircle className="w-4 h-4 text-gray-600" />;
        }
    };

    const getApprovalStatus = (approved) => {
        if (approved === true) return { text: 'Approved', color: 'text-green-600 bg-green-50' };
        if (approved === false) return { text: 'Rejected', color: 'text-red-600 bg-red-50' };
        return { text: 'Pending', color: 'text-yellow-600 bg-yellow-50' };
    };

    return (
        <div className="min-h-screen bg-white p-6 print:p-4">
            <style jsx>{`
        @media print {
            @page {
                size: A3 portrait;
                margin: 1cm;
            }
            .page-break {
                page-break-before: always;
                break-before: page;
            }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>

            {recceData.map((recce, index) => (
                <div key={recce._id} className={`${index > 0 ? 'page-break' : ''} mb-8 print:mb-4`}>
                    {/* Header */}
                    <div className="border-b-4 border-blue-600 pb-4 mb-6 print:mb-4">
                        <div className="flex justify-between items-start">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 print:text-2xl">RECCE REPORT</h1>
                                <p className="text-lg text-gray-600 mt-1">{recce.brand} - {recce.channel_programme}</p>
                            </div>
                            <div className="text-right">
                                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                                    {getStatusIcon(recce.status)}
                                    <span className="font-semibold uppercase">{recce.status}</span>
                                </div>
                                <p className="text-sm text-gray-500">ID: {recce._id.slice(-8)}</p>
                                <p className="text-sm text-gray-500">Created: {formatDate(recce.createdAt)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Main Content Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 print:gap-4">

                        {/* Left Column - Outlet & Vendor Info */}
                        <div className="space-y-6 print:space-y-4">

                            {/* Outlet Details */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 print:p-3">
                                <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                                    <Building2 className="w-5 h-5" />
                                    Outlet Information
                                </h3>
                                <div className="space-y-2 text-sm">
                                    <div><span className="font-medium">Name:</span> {recce.outletWorkDetails?.outletName}</div>
                                    <div className="flex justify-center items-start gap-1">
                                        <MapPin className="w-4 h-4 mt-0.5 text-gray-500" />
                                        <span>{recce.outletWorkDetails?.address}, {recce.outletWorkDetails?.town}</span>
                                    </div>
                                    <div><span className="font-medium">PIN:</span> {recce.outletWorkDetails?.pinCode}</div>
                                    <div><span className="font-medium">GCC:</span> {recce.outletWorkDetails?.gccNum}</div>
                                </div>
                            </div>

                            {/* Vendor Details */}
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 print:p-3">
                                <h3 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                                    <User className="w-5 h-5" />
                                    Vendor Information
                                </h3>
                                <div className="space-y-2 text-sm">
                                    <div><span className="font-medium">Name:</span> {recce.userData?.userName}</div>
                                    <div><span className="font-medium">Company:</span> {recce.userData?.companyName}</div>
                                    <div className="flex justify-center items-center gap-1">
                                        <Phone className="w-4 h-4 text-gray-500" />
                                        <span>{recce.userData?.phoneNum}</span>
                                    </div>
                                    <div className="flex justify-center items-center gap-1">
                                        <Mail className="w-4 h-4 text-gray-500" />
                                        <span className="break-all">{recce.userData?.email}</span>
                                    </div>
                                    <div><span className="font-medium">Location:</span> {recce.userData?.city}, {recce.userData?.state}</div>
                                </div>
                            </div>
                        </div>

                        {/* Right Columns - Job Details */}
                        <div className="lg:col-span-2">
                            {recce.jobs?.map((job, jobIndex) => (
                                <div key={job._id} className={`${jobIndex > 0 ? 'page-break' : ''} border border-gray-300 rounded-lg overflow-hidden mb-6`}>

                                    {/* Job Header */}
                                    <div className="bg-gray-800 text-white p-4 print:p-3">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h3 className="font-bold text-lg">{job.type}</h3>
                                                <p className="text-gray-300 text-sm">Task Type: {job.taskType}</p>
                                            </div>
                                            <div className="text-right text-sm">
                                                <div className="text-gray-300">Job {jobIndex + 1}</div>
                                                <div className="text-gray-400">{job._id.slice(-8)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Before & After Comparison */}
                                    <div className="grid grid-cols-1 md:grid-cols-2">

                                        {/* Before Section */}
                                        <div className="border-r border-gray-200 p-4 print:p-3">
                                            <h4 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                                                <Camera className="w-4 h-4" />
                                                BEFORE
                                            </h4>

                                            {/* Before Images */}
                                            <div className="grid grid-cols-2 gap-2 mb-3">
                                                {job.before?.images?.slice(0, 4).map((img, imgIndex) => (
                                                    <a target="_blank" href={img}>
                                                        <img key={imgIndex} src={img} alt={`Before ${imgIndex + 1}`}
                                                             className="w-full h-80 object-contain rounded border" />
                                                    </a>
                                                ))}
                                                {/* Before Details */}
                                                <div className="space-y-2 text-sm p-4 rounded border">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">Dimensions:</span>
                                                        <span>{job.before?.geometryData?.width?.toFixed(2)}m × {job.before?.geometryData?.length?.toFixed(2)}m</span>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <Clock className="w-4 h-4 text-gray-500" />
                                                        <span>{formatDate(job.before?.actionTimestamp)}</span>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <MapPin className="w-4 h-4 text-gray-500" />
                                                        <span className="text-xs">{job.before?.latlong}</span>
                                                    </div>

                                                    {job.before?.approved !== null && (
                                                        <div className={`px-2 py-1 rounded text-xs ${getApprovalStatus(job.before.approved).color}`}>
                                                            {getApprovalStatus(job.before.approved).text}
                                                        </div>
                                                    )}

                                                    {job.before?.remark && (
                                                        <div>
                                                            <span className="font-medium">Vendor Remark:</span>
                                                            <p className="text-gray-600 mt-1">{job.before.remark}</p>
                                                        </div>
                                                    )}

                                                    {job.before?.rejectedReason && (
                                                        <div className="bg-red-50 p-2 rounded">
                                                            <span className="font-medium text-red-700">Admin Remark:</span>
                                                            <p className="text-red-600 text-xs mt-1">{job.before.rejectedReason}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>


                                        </div>

                                        {/* After Section */}
                                        <div className="p-4 print:p-3">
                                            <h4 className="font-bold text-green-700 mb-3 flex items-center gap-2">
                                                <Camera className="w-4 h-4" />
                                                AFTER
                                            </h4>

                                            {/* After Images */}
                                            <div className="grid grid-cols-2 gap-2 mb-3">
                                                {job.after?.images?.slice(0, 4).map((img, imgIndex) => (
                                                    <a target="_blank" href={img}>
                                                        <img key={imgIndex} src={img} alt={`After ${imgIndex + 1}`}
                                                             className="w-full h-80 object-contain rounded border" />
                                                    </a>
                                                ))}

                                                {/* After Details */}
                                                <div className="space-y-2 text-sm p-4 rounded border">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">Dimensions:</span>
                                                        <span>{job.after?.geometryData?.width?.toFixed(2)}m × {job.after?.geometryData?.length?.toFixed(2)}m</span>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <Clock className="w-4 h-4 text-gray-500" />
                                                        <span>{formatDate(job.after?.actionTimestamp || job.after?.updatedAt)}</span>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <MapPin className="w-4 h-4 text-gray-500" />
                                                        <span className="text-xs">{job.after?.latlong}</span>
                                                    </div>

                                                    {job.after?.approved !== null && (
                                                        <div className={`px-2 py-1 rounded text-xs ${getApprovalStatus(job.after.approved).color}`}>
                                                            {getApprovalStatus(job.after.approved).text}
                                                        </div>
                                                    )}

                                                    {job.after?.remark && (
                                                        <div>
                                                            <span className="font-medium">Remark:</span>
                                                            <p className="text-gray-600 mt-1">{job.after.remark}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>


                                        </div>
                                    </div>

                                    {/* Design Image */}
                                    {job.designImage && job.designImage.length > 0 && (
                                        <div className="border-t border-gray-200 p-4 print:p-3 bg-gray-50">
                                            <h4 className="font-medium mb-2 flex items-center gap-2">
                                                <FileText className="w-4 h-4" />
                                                Design Reference
                                            </h4>
                                            <img src={job.designImage[0]} alt="Design" className="max-w-full h-24 object-contain rounded border" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 pt-4 mt-6 print:mt-4 text-center">
                        <p className="text-sm text-gray-500">
                            Report generated on {new Date().toLocaleDateString('en-IN')} |
                            Last updated: {formatDate(recce.updatedAt)}
                        </p>
                    </div>
                </div>
            ))}

            {/* Print Button */}
            <div className="fixed bottom-6 right-6 print:hidden">
                <button
                    onClick={() => window.print()}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg shadow-lg font-medium transition-colors"
                >
                    Print PDF
                </button>
            </div>
        </div>
    );
};

export default ReceePrintPage;